import { useState } from 'react';
import { RunButton, InfoCard, NoResult, AiAdvisor } from './common';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { HiResPanel } from './HiResPanel';

const Suitability = ({ farm, crops, year, season, selectedCrop, setSelectedCrop }) => {
  // ---- Soil-Climatic (Hi-Res) crop selection state -------------------------
  const [hires, setHires] = useState(null);
  const [hiresLoading, setHiresLoading] = useState(false);
  const [irrigation, setIrrigation] = useState(farm.irrigation_availability || 'full_season');
  const [budget, setBudget] = useState('');            // ₹ per hectare, blank = no constraint
  const [labour, setLabour] = useState('medium');      // low | medium | high

  // ---- Stage-2 DNDC state --------------------------------------------------
  const [dndcCrop, setDndcCrop] = useState(selectedCrop || crops[0]?.key);
  const [dndc, setDndc] = useState(null);
  const [dndcLoading, setDndcLoading] = useState(false);

  const runHires = async () => {
    setHiresLoading(true);
    try {
      const r = await api.cropSelectionHires({
        lat: farm.lat, lon: farm.lon,
        polygon: farm.parcel_ring || null,
        irrigation_availability: irrigation,
        budget_rs_per_ha: budget ? parseFloat(budget) : null,
        labour_availability: labour,
      });
      setHires(r);
      toast.success(`Ranking ready — top crop: ${r.top_pick?.crop}`);
    } catch (e) { toast.error('Soil-Climatic crop selection failed'); }
    finally { setHiresLoading(false); }
  };

  const runDndc = async () => {
    setDndcLoading(true);
    setSelectedCrop(dndcCrop);
    try {
      const r = await api.dndc({
        crop_key: dndcCrop, lat: farm.lat, lon: farm.lon,
        year, season,
        irrigation_availability: farm.irrigation_availability,
      });
      setDndc(r);
      toast.success(`DNDC results ready for ${crops.find(c => c.key === dndcCrop)?.label || dndcCrop}`);
    } catch (e) { toast.error('DNDC run failed'); }
    finally { setDndcLoading(false); }
  };

  return (
    <>
      <InfoCard title="Soil-Climatic Crop Selection">
        <div className="grid gap-3 md:grid-cols-4 items-end">
          <div>
            <label className="text-xs text-icrisat-earth block mb-1">Irrigation availability</label>
            <Select value={irrigation} onValueChange={setIrrigation}>
              <SelectTrigger data-testid="hires-irrigation-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="full_season">Full season (assured)</SelectItem>
                <SelectItem value="supplemental">Supplemental (limited)</SelectItem>
                <SelectItem value="rainfed">Rainfed (none)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-icrisat-earth block mb-1">Budget (₹ / hectare)</label>
            <Input type="number" placeholder="e.g. 40000"
                   data-testid="hires-budget-input"
                   value={budget} onChange={e => setBudget(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-icrisat-earth block mb-1">Labour availability</label>
            <Select value={labour} onValueChange={setLabour}>
              <SelectTrigger data-testid="hires-labour-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low · family only (~60 man-days/ha)</SelectItem>
                <SelectItem value="medium">Medium · family + limited hired (~120)</SelectItem>
                <SelectItem value="high">High · freely hired (~250)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <RunButton testId="run-hires-selection-btn" onClick={runHires} loading={hiresLoading}>
            Rank crops
          </RunButton>
        </div>
        <div className="text-[11px] text-muted-foreground mt-2">
          Farm: <b>{farm.name}</b> · ({farm.lat.toFixed(3)}, {farm.lon.toFixed(3)}) ·
          Budget blank means no constraint · Exceeding budget demotes crop by 60%; exceeding labour by 50%.
        </div>
        {!hires && !hiresLoading && <div className="mt-4"><NoResult /></div>}
        {hires && <HiResPanel data={hires} />}
      </InfoCard>

      <InfoCard title="Stage 2 · DNDC yield + GHG + economics">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px]">
            <label className="text-xs text-icrisat-earth block mb-1">Crop</label>
            <Select value={dndcCrop} onValueChange={setDndcCrop}>
              <SelectTrigger data-testid="dndc-crop-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {crops.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <RunButton testId="run-dndc-btn" onClick={runDndc} loading={dndcLoading}>Run DNDC</RunButton>
          <span className="text-xs text-muted-foreground">
            Season: <b>{season} {year}</b> · Irrigation: <b>{farm.irrigation_availability}</b>
          </span>
        </div>

        {dndc && (
          <div className="mt-5 animate-fade-up space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kvc label="Yield" value={dndc.yield_kg_ha?.toLocaleString('en-IN')} unit="kg/ha" />
              <Kvc label="Total CO2-eq" value={dndc.total_CO2eq_kg_ha?.toLocaleString('en-IN')} unit="kg/ha" />
              <Kvc label="CH4" value={dndc.CH4_kg_ha} unit="kg/ha" />
              <Kvc label="N2O" value={dndc.N2O_kg_ha} unit="kg/ha" />
              <Kvc label="Gross revenue" value={`₹${dndc.economics.gross_revenue_rs_ha?.toLocaleString('en-IN')}`} unit="/ha" />
              <Kvc label="Input cost" value={`₹${dndc.economics.input_cost_rs_ha?.toLocaleString('en-IN')}`} unit="/ha" />
              <Kvc label="Net return" value={`₹${dndc.economics.net_return_rs_ha?.toLocaleString('en-IN')}`} unit="/ha" green={dndc.economics.net_return_rs_ha > 0} />
              <Kvc label="B/C ratio" value={dndc.economics.benefit_cost_ratio} unit="" />
            </div>
            <p className="text-xs text-muted-foreground">
              Sowing date used: <b>{dndc.sowing_date}</b> · Duration: {dndc.duration_days} days · {dndc.yield_unit_note}
            </p>
            <AiAdvisor context="dndc" payload={dndc} />
          </div>
        )}
        {dndcLoading && !dndc && (
          <div className="mt-4 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Running DNDC…
          </div>
        )}
        {!dndc && !dndcLoading && <div className="mt-4"><NoResult /></div>}
      </InfoCard>
    </>
  );
};

const Kvc = ({ label, value, unit, green }) => (
  <div className="rounded-lg bg-icrisat-green-soft/70 border border-icrisat-green/20 p-3">
    <div className="text-[10px] uppercase tracking-wider text-icrisat-earth">{label}</div>
    <div className={`font-display text-xl mt-1 ${green ? 'text-emerald-700' : 'text-icrisat-green'}`}>
      {value ?? '—'} {unit && <span className="text-xs font-normal text-icrisat-earth">{unit}</span>}
    </div>
  </div>
);

export default Suitability;
